import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getMockUser, signIn, signUp } from "@/lib/auth";
import { isSupabaseConfigured } from "@/lib/supabase";
import { toast } from "sonner";

export const Route = createFileRoute("/login")({
  beforeLoad: () => {
    if (typeof window !== "undefined" && getMockUser()) {
      throw redirect({ to: "/dashboard" });
    }
  },
  component: LoginPage,
  head: () => ({ meta: [{ title: "Sign in — CSaaS" }] }),
});

type Mode = "signin" | "signup";

function LoginPage() {
  const navigate = useNavigate();
  const supabaseOn = isSupabaseConfigured();
  const [mode, setMode] = useState<Mode>("signin");
  const [email, setEmail] = useState(supabaseOn ? "" : "alex@acme.dev");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "signup" && supabaseOn) {
        await signUp(email, password, name || undefined);
        toast.success("Account created. Check your inbox to confirm, then sign in.");
        setMode("signin");
      } else {
        await signIn(email, password);
        navigate({ to: "/dashboard" });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-primary p-10 text-primary-foreground">
        <div className="flex items-center gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-md bg-primary-foreground/15 font-display font-semibold">
            C
          </div>
          <span className="font-display text-lg font-semibold">CSaaS</span>
        </div>
        <div className="space-y-3 max-w-md">
          <h1 className="font-display text-3xl font-semibold leading-tight">
            Run Claude Code skills, on demand.
          </h1>
          <p className="text-sm text-primary-foreground/80">
            Submit a skill and a prompt, get a streamed result. Backed by a high-throughput async
            worker pool, isolated per job.
          </p>
        </div>
        <p className="text-xs text-primary-foreground/60">
          {supabaseOn ? "Supabase auth" : "Demo build · mock auth"}
        </p>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6">
          <div className="space-y-1">
            <h2 className="font-display text-2xl font-semibold">
              {mode === "signup" ? "Create account" : "Sign in"}
            </h2>
            <p className="text-sm text-muted-foreground">
              {supabaseOn
                ? mode === "signup"
                  ? "Use your work email — confirmation will be sent."
                  : "Use your Supabase email + password."
                : "Mock auth — any email works."}
            </p>
          </div>

          {mode === "signup" && supabaseOn && (
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {supabaseOn && (
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          )}

          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Working…" : mode === "signup" ? "Create account" : "Continue"}
          </Button>

          {supabaseOn && (
            <p className="text-center text-xs text-muted-foreground">
              {mode === "signup" ? "Already have an account? " : "Need an account? "}
              <button
                type="button"
                className="text-foreground underline-offset-2 hover:underline"
                onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              >
                {mode === "signup" ? "Sign in" : "Create one"}
              </button>
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
